/**
 * Plan entitlements. Free is the whole page in text + images; Premium unlocks
 * moving pictures — a video intro, per-project videos, and tailored video
 * applications. The single source of truth for what each tier can do.
 *
 * Tier lives on profiles.tier ('free' | 'paid'), flipped by billing (Stripe,
 * Phase 4) or the beta "activate premium" action until checkout is live.
 */

/**
 * Premium video-application allowance.
 *
 * 50, matching Change Maker (CM-394). It was 25 while Finseats charged $52/yr
 * and Change Maker charged $42/yr for 50 — a member paying 24% MORE for half
 * the allowance, on two boards that share this file, sit in the same portfolio
 * and are one search apart. That was an accident of two separate edits, not a
 * positioning decision anybody made.
 *
 * The marginal-cost question was the one thing genuinely worth checking before
 * doubling the allowance on the single feature with a real per-unit cost (Mux
 * storage + streaming + per-application talking-point generation). Measured on
 * the production databases 2026-09-11: financecareers has 15 profiles, all
 * `free`, and ZERO rows in video_applications; internationaljobs has 1 paid
 * profile and also zero. Nobody is consuming any allowance, so the marginal
 * cost of raising the cap today is exactly $0 — and the two boards share ONE
 * Mux environment, so the bill stays legible either way. Revisit if paid
 * members ever approach the cap; until then "more money, less product" standing
 * in public is the expensive option.
 */
export const VIDEO_APPLICATION_QUOTA = 50;

/** Maximum length of a video application, in seconds. */
export const VIDEO_APPLICATION_MAX_SECONDS = 120;

/*
 * Pricing (changed 2026-09-01: was a flat "$35/yr").
 *
 * We SHOW a monthly figure because that is how people judge whether a
 * subscription is cheap, but we BILL once a year. Those are two different
 * numbers, so the rule is: any surface showing the monthly figure must show
 * the annual charge next to it. PREMIUM_PRICE_AMOUNT is the ONLY number Stripe
 * ever sees — checkout builds price_data inline from it, so changing it here
 * changes what is actually charged, with no Stripe dashboard step.
 *
 * $5/mo, billed annually at $60 — Jussi's price (2026-09-21; was $4.50/mo
 * billed at $52). $5 x 12 is exactly $60, so the monthly headline and the
 * charge agree. `pnpm check:pricing` enforces that the customer never pays
 * MORE than the monthly figure implies.
 */

/** What Stripe charges for the annual Premium subscription, in cents. */
export const PREMIUM_PRICE_AMOUNT = 6000;
export const PREMIUM_PRICE_CURRENCY = "usd";

/** The annual charge, spelled out. Derived, so it cannot drift from the charge. */
export const PREMIUM_PRICE_ANNUAL_LABEL = `$${PREMIUM_PRICE_AMOUNT / 100}`;
/** Headline Premium price, shown on every upgrade CTA. */
export const PREMIUM_PRICE_LABEL = "$5/mo";
/** Longer form for prose ("… for $5 a month"). */
export const PREMIUM_PRICE_LABEL_LONG = "$5 a month";
/** Always pair a monthly figure with this, so the real charge is never a surprise. */
export const PREMIUM_BILLING_NOTE = `billed annually at ${PREMIUM_PRICE_ANNUAL_LABEL}`;
/** Billing interval passed to Stripe (price_data.recurring.interval). Must be a
 *  literal for Stripe's typed params. */
export const PREMIUM_INTERVAL = "year" as const;

export interface TierBearer {
  tier?: string | null;
}

/** Whether this profile/account is on the paid plan. */
export function isPaid(bearer: TierBearer | null | undefined): boolean {
  return bearer?.tier === "paid";
}

/** What free is missing (used for upgrade copy). */
export const PREMIUM_FEATURES = [
  "A 90-second video introduction on your page",
  "Video on your featured projects",
  `${VIDEO_APPLICATION_QUOTA} tailored video applications`,
  "Page analytics as it ships",
] as const;
